'use client'

import { useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { createTRPCClient, httpBatchLink } from '@trpc/client'
import { createTRPCContext } from '@trpc/tanstack-react-query'
import SuperJSON from 'superjson'

import type { AppRouter } from '@package/api'
import { SharedTRPCProvider } from '@ui/shared/context'

import { createQueryClient } from '~/react-query/query-client'

const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>()

function makeTRPCClient() {
  return createTRPCClient<AppRouter>({
    links: [
      httpBatchLink({
        transformer: SuperJSON,
        // eslint-disable-next-line no-restricted-properties
        url: `${process.env.NEXT_PUBLIC_API_URL}/api/trpc`,
        fetch(url, options) {
          return fetch(url, { ...options, credentials: 'include' })
        },
      }),
    ],
  })
}

let trpcClientSingleton: ReturnType<typeof makeTRPCClient> | undefined
const getTRPCClient = () => {
  if (typeof window === 'undefined') return makeTRPCClient()
  return (trpcClientSingleton ??= makeTRPCClient())
}

let queryClientSingleton: ReturnType<typeof createQueryClient> | undefined
const getQueryClient = () => {
  if (typeof window === 'undefined') return createQueryClient()
  return (queryClientSingleton ??= createQueryClient())
}

function TRPCContextInner({ children }: { children: React.ReactNode }) {
  const trpc = useTRPC()
  return <SharedTRPCProvider trpc={trpc}>{children}</SharedTRPCProvider>
}

export function ExploreTRPCProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(getQueryClient)
  const [trpcClient] = useState(getTRPCClient)

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        <TRPCContextInner>{children}</TRPCContextInner>
      </TRPCProvider>
    </QueryClientProvider>
  )
}
